/**
 * Лента непрочитанных уведомлений. Одна и та же в поповере колокольчика и на
 * странице `/notifications`, поэтому высота задаётся снаружи классом.
 */
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  notificationLink,
  notificationText,
  notificationTitle,
  useNotificationActionsV2,
  useNotificationsV2,
  type NotificationV2,
} from '@/entities/notification';
import { getApiErrorMessage } from '@/shared/api';
import { Skeleton } from '@/shared/ui';

function relativeTime(value: unknown): string {
  const raw = typeof value === 'string' ? value : '';
  const date = raw ? new Date(raw) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }

  return formatDistanceToNow(date, { addSuffix: true, locale: ru });
}

type NotificationRowProps = {
  notification: NotificationV2;
  isBusy: boolean;
  onRead: (id: string) => void;
  onNavigate: () => void;
};

function NotificationRow({
  notification,
  isBusy,
  onRead,
  onNavigate,
}: NotificationRowProps) {
  const navigate = useNavigate();
  const link = notificationLink(notification.payload);
  const text = notificationText(notification.payload);
  const time = relativeTime(notification.createdAt);

  const open = () => {
    if (!link) {
      return;
    }
    onNavigate();
    navigate(link);
  };

  return (
    <div className="flex gap-3 rounded-[12px] px-3 py-2.5 transition-colors hover:bg-[#F6F7FA]">
      <button
        type="button"
        onClick={open}
        disabled={!link}
        className="min-w-0 flex-1 text-left disabled:cursor-default"
      >
        <div className="truncate text-[14px] font-medium text-[#17244A]">
          {notificationTitle(notification.type)}
        </div>
        {text && (
          <div className="mt-0.5 truncate text-[13px] text-[#6B7280]">
            {text}
          </div>
        )}
        {time && <div className="mt-1 text-[12px] text-[#9AA3B2]">{time}</div>}
      </button>

      <button
        type="button"
        title="Отметить прочитанным"
        aria-label="Отметить прочитанным"
        disabled={isBusy || !notification.id}
        onClick={() => onRead(String(notification.id))}
        className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full border border-[#E7EAF0] text-[#6B7280] transition-colors hover:border-[#102653] hover:text-[#102653] disabled:opacity-40"
      >
        <Check className="size-4" strokeWidth={2} />
      </button>
    </div>
  );
}

type NotificationListProps = {
  /** Классы контейнера ленты — снаружи задаются высота и скролл. */
  className?: string;
  /** Вызывается перед переходом по уведомлению (поповер закрывается). */
  onNavigate?: () => void;
};

export function NotificationList({
  className = '',
  onNavigate,
}: NotificationListProps) {
  const { data, isLoading, isError } = useNotificationsV2();
  const { markRead, isBusy } = useNotificationActionsV2();

  const handleRead = (id: string) => {
    markRead.mutate(id, {
      onError: (error) =>
        toast.error(
          getApiErrorMessage(error, 'Не удалось отметить уведомление')
        ),
    });
  };

  if (isLoading) {
    return (
      <div className={`flex flex-col gap-2 p-3 ${className}`}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-[12px]" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className={`px-4 py-8 text-center text-[13px] text-[#9AA3B2] ${className}`}
      >
        Не удалось загрузить уведомления
      </div>
    );
  }

  const items = data ?? [];

  if (items.length === 0) {
    return (
      <div
        className={`px-4 py-8 text-center text-[13px] text-[#9AA3B2] ${className}`}
      >
        Непрочитанных уведомлений нет
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1 p-2 ${className}`}>
      {items.map((notification) => (
        <NotificationRow
          key={String(notification.id)}
          notification={notification}
          isBusy={isBusy}
          onRead={handleRead}
          onNavigate={() => onNavigate?.()}
        />
      ))}
    </div>
  );
}
